import { useMemo } from 'react'
import { round } from 'shared/util'
import { PinIcon } from 'shared/icons'
import { colors } from 'shared/colors'

import { useSearch } from 'features/search/context'
import { KojinResult } from 'interface/kojin'
import { Box, BoxProps, LinearProgress, Stack, Typography } from "@mui/material"

import { BlockCaption } from 'components/controls/blockCaption'
import { ValueSpan } from 'components/controls/valueSpan'
import { LimittedList } from 'components/controls/limittedList'

type DiseaseRecordBySource = {
	dataSource: string
	score: number
}

type DiseaseRecord = {
	icd10code: string
	nodeName?: string
	score: number
	bySources: DiseaseRecordBySource[]
}

type KojinDiseasesBlockProps = {
	diseases: KojinResult["diseases"],
	limit: number
} & BoxProps
export const KojinDiseasesBlock = ({
	diseases,
	limit,
	...others
}: KojinDiseasesBlockProps) => {
	const search = useSearch()
	const summary = useMemo<{
		list: DiseaseRecord[]
		bySources: DiseaseRecordBySource[]
		maxScore: number
		totalScore: number
	}>(
		() => {
			const list: DiseaseRecord[] = []
			const bySources: DiseaseRecordBySource[] = []
			let totalScore = 0
			let maxScore = 0
			diseases?.forEach((disease) => {
				maxScore = Math.max(maxScore, disease.score)
				totalScore += disease.score
				let record = list.find(record => record.icd10code === disease.icd10code)
				if (!record) {
					record = { icd10code: disease.icd10code, nodeName: disease.nodeName, score: 0, bySources: [] }
					list.push(record)
				}
				record.score = record.score + disease.score
	
				let source = record.bySources.find(source => source.dataSource === disease.dataSource)
				if (!source) {
					source = { dataSource: disease.dataSource, score: 0 }
					record.bySources.push(source)
				}
				source.score = source.score + disease.score
	
				source = bySources.find(source => source.dataSource === disease.dataSource)
				if (!source) {
					source = { dataSource: disease.dataSource, score: 0 }
					bySources.push(source)
				}
				source.score = source.score + disease.score
			})
			list.sort((a, b) => b.score - a.score)
			bySources.sort((a, b) => b.score - a.score)
			list.forEach(record => record.bySources.sort((a, b) => {
				const indexA = bySources.findIndex(s => s.dataSource === a.dataSource)
				const indexB = bySources.findIndex(s => s.dataSource === b.dataSource)
				return indexA - indexB
			}))
			return {
				list,
				bySources,
				maxScore,
				totalScore
			}
		},
		[diseases]
	)
	return (
		<Box {...others}>
			{summary.list.length > 0 ? (
				<Box>
					<BlockCaption
						caption=''
						rightElement={summary.bySources.length > 0 ? (
							<Stack
								direction='row'
								spacing={2}
								flexWrap="wrap"
								useFlexGap
								justifyContent='flex-end'
							>
								{summary.bySources.map((source, i) => (
									<ValueSpan
										key={`source-${i}-${source.dataSource}`}
										pin={<PinIcon style={{
											color: (
												source.dataSource === 'news' ? colors.news.main :
												source.dataSource === 'mrp' ? colors.mrp.main :
												source.dataSource === 'picks' ? colors.picks.main :
												undefined
											)
										}}/>}
										prefix={(
											source.dataSource === 'news' ? 'NEWS' :
											source.dataSource === 'mrp' ? 'MRPlus' :
											source.dataSource === 'picks' ? 'DrsPicks' :
											source.dataSource
										)}
										text={round((source.score * 100) / summary.totalScore, 1).toString()}
										suffix='%'
										prefixProps={{ whiteSpace: 'nowrap' }}
									/>
								)) }
							</Stack>
						) : undefined}
					/>

					<Stack direction='column' paddingTop={1}>
						<LimittedList
							keyPrefix='disease'
							list={summary.list}
							count={limit}
							record={(disease, i) => {
								const sd = search?.disease?.find((d) => d.icd10code?.startsWith(disease.icd10code))
								return (
									<Stack direction='row' paddingX={2} spacing={1} alignItems="flex-start" width='100%' boxSizing="border-box">
										<Typography variant="body2" component="div" color='text.secondary' textAlign='right'>{disease.icd10code}</Typography>
										{ sd ? (
											<Typography variant="body2" component="div" color='text.primary' flexGrow={1} fontWeight='bold'>{sd.diseaseName || disease.nodeName || 'unknown'}</Typography>
										) : (
											<Typography variant="body2" component="div" color='text.primary' flexGrow={1}>{disease.nodeName || 'unknown'}</Typography>
										) }
										{ summary.maxScore > 0 && (
											<Stack direction='row' width='50%' position='relative' justifyItems='flex-start' flexShrink={0} paddingY="2px">
												{ disease.bySources.map((ds, j) => (
													<LinearProgress
														key={`dscore-${i}-${j}-${ds.dataSource}`}
														variant="determinate"
														color={(
															ds.dataSource === 'news' ? 'news' :
															ds.dataSource === 'mrp' ? 'mrp' :
															ds.dataSource === 'picks' ? 'picks' :
															'primary'
														)}
														value={100}
														sx={{
															width: `${(ds.score * 100) / summary.maxScore}%`,
															height: '1rem',
															borderTopLeftRadius: j === 0 ? '0.2rem' : 0,
															borderBottomLeftRadius: j === 0 ? '0.2rem' : 0,
															borderTopRightRadius: j === (disease.bySources.length-1) ? '0.2rem' : 0,
															borderBottomRightRadius: j === (disease.bySources.length-1) ? '0.2rem' : 0,
														}}
													/>
												)) }
											</Stack>
										) }
									</Stack>
								)
							}}
						/>
					</Stack>
				</Box>
			) : <></>}
		</Box>
	)
}
